import {
	ColumnModeTypeEnum,
	createColumnEditor,
	SuperGridColDef,
} from '@aleph/supergrid';
import {
	ImageIcon,
	VideoIcon,
	CarouselIcon,
	TextIcon,
	DefaultCellRenderer,
	SelectCellEditor,
	WiseBlueCheckboxCellRenderer,
	translate,
} from '@aleph/shared-components';
import { editableOnThisLevel, getOptionsFromEnum } from 'wb-reddit/app/routes/superGrid/helpers/index';
import {
	CampaignObjective, CreativeContentCallToAction, CreativeType, EventTrackerInput, EventTrackerType, Media,
} from 'wb-reddit/graphql/generated';
import RedditMediaCellRenderer from 'wb-reddit/app/routes/superGrid/cellRenderers/media';
import RedditMediaCellEditor from 'wb-reddit/app/routes/superGrid/columnEditors/media/cell';
import RedditMediaColumnEditor, { doesMediaMatchType } from 'wb-reddit/app/routes/superGrid/columnEditors/media/column';
import URLsCellEditor from 'wb-reddit/app/routes/superGrid/columnEditors/urls/cell';
import URLsColumnEditor from 'wb-reddit/app/routes/superGrid/columnEditors/urls/column';
import UrlsCellRenderer, {
	IUrlsCellRendererParams,
} from 'wb-reddit/app/routes/superGrid/cellRenderers/urls';
import RedditThumbnailCellRenderer from 'wb-reddit/app/routes/superGrid/cellRenderers/thumbnail';
import { flatten, isArray, isFunction } from 'lodash';
import { adHeaderNames } from 'wb-reddit/app/routes/superGrid/columnDefinitions/headerNames';
import CreativeContentDestinationUrlCellRenderer
	from 'wb-reddit/app/routes/superGrid/cellRenderers/creativeContentDestinationUrl';
import { SelectRenderer } from 'wb-reddit/app/routes/superGrid/columnDefinitions/utils';
import { EditableCallbackParams, ICellRendererParams, EditableCallback } from 'ag-grid-community';

export type AdMedia = Partial<Media> | undefined | null;

export const getCreativeTypeByObjective = (objective: CampaignObjective) => {
	switch (objective) {
		case CampaignObjective.Clicks:
			return [
				{ icon: ImageIcon({ fill: 'hsl(var(--blue-500))', width: 16, height: 16 }), value: CreativeType.Image, label: translate('sc: creative_type.image', { defaultValue: 'Image' }) },
				{ icon: VideoIcon({ fill: 'hsl(var(--blue-500))', width: 16, height: 16 }), value: CreativeType.Video, label: translate('sc: creative_type.video', { defaultValue: 'Video' }) },
				{ icon: CarouselIcon({ fill: 'hsl(var(--blue-500))', width: 16, height: 16 }), value: CreativeType.Carousel, label: translate('sc: creative_type.carousel', { defaultValue: 'Carousel' }) },
				{ icon: TextIcon({ fill: 'hsl(var(--blue-500))', width: 16, height: 16 }), value: CreativeType.Text, label: translate('sc: creative_type.text', { defaultValue: 'Text' }) },
			];
		case CampaignObjective.Conversions:
		case CampaignObjective.AppInstalls:
			return [
				{ icon: ImageIcon({ fill: 'hsl(var(--blue-500))', width: 16, height: 16 }), value: CreativeType.Image, label: translate('sc: creative_type.image', { defaultValue: 'Image' }) },
				{ icon: VideoIcon({ fill: 'hsl(var(--blue-500))', width: 16, height: 16 }), value: CreativeType.Video, label: translate('sc: creative_type.video', { defaultValue: 'Video' }) },
				{ icon: CarouselIcon({ fill: 'hsl(var(--blue-500))', width: 16, height: 16 }), value: CreativeType.Carousel, label: translate('sc: creative_type.carousel', { defaultValue: 'Carousel' }) },
			];
		case CampaignObjective.VideoViewableImpressions:
			return [
				{ icon: VideoIcon({ fill: 'hsl(var(--blue-500))', width: 16, height: 16 }), value: CreativeType.Video, label: translate('sc: creative_type.video', { defaultValue: 'Video' }) },
			];
		case CampaignObjective.Impressions:
			return [
				{ icon: ImageIcon({ fill: 'hsl(var(--blue-500))', width: 16, height: 16 }), value: CreativeType.Image, label: translate('sc: creative_type.image', { defaultValue: 'Image' }) },
				{ icon: VideoIcon({ fill: 'hsl(var(--blue-500))', width: 16, height: 16 }), value: CreativeType.Video, label: translate('sc: creative_type.video', { defaultValue: 'Video' }) },
				{ icon: CarouselIcon({ fill: 'hsl(var(--blue-500))', width: 16, height: 16 }), value: CreativeType.Carousel, label: translate('sc: creative_type.carousel', { defaultValue: 'Carousel' }) },
				{ icon: TextIcon({ fill: 'hsl(var(--blue-500))', width: 16, height: 16 }), value: CreativeType.Text, label: translate('sc: creative_type.text', { defaultValue: 'Text' }) },
			];
	}

	return [];
};

const creativeFieldEditable = ({ data }: EditableCallbackParams | ICellRendererParams) => (data?.creativeId && data?.creativeHasErrors && !data?.externalCreativeId) || data?.isInCreation;

const carouselEditable = (params: EditableCallbackParams | ICellRendererParams, index: number, isMediaUrlCell = false) => {
	const isFirst = index === 0;

	if (isFirst) {
		return editableOnThisLevel(params) && creativeFieldEditable(params);
	}

	return editableOnThisLevel(params)
		&& params?.data?.[`creativeContentMediaUrl${isMediaUrlCell ? (index - 1) : index}`]
		&& params?.data?.creativeType === CreativeType.Carousel
		&& creativeFieldEditable(params);
};

const carouselDisabled = (params: EditableCallbackParams | ICellRendererParams, index: number, isMediaUrlCell = false) => {
	const isFirst = index === 0;

	if (isFirst) {
		return !editableOnThisLevel(params);
	}

	return !editableOnThisLevel(params)
		|| params?.data?.creativeType !== CreativeType.Carousel
		|| !params?.data?.[`creativeContentMediaUrl${isMediaUrlCell ? (index - 1) : index}`];
};

const carouselConditionallyVisible = ({ allNodesData }, index) => {
	if (index === 0) {
		return true;
	}

	return allNodesData?.some(nodeData => nodeData?.creativeType === CreativeType.Carousel);
};

const carouselTemplate = (
	index: number,
): SuperGridColDef[] => [
	{
		columnMode:   ColumnModeTypeEnum.BOTH,
		field:        `creativeContentMediaUrl${index}`,
		headerName:   `${adHeaderNames.creativeContentMediaUrl} ${index + 1}`,
		elasticChild: 'settingsElastic',
		width:        320,
		editable:     params => carouselEditable(params, index, true)
			&& params?.data?.creativeType !== CreativeType.Text,
		cellRenderer:         RedditMediaCellRenderer,
		conditionallyVisible: (params) => carouselConditionallyVisible(params, index),
		cellRendererParams:   params => ({
			disabled: !params.value && (
				carouselDisabled(params, index, true)
				|| !creativeFieldEditable(params)
				|| params?.data?.creativeType === CreativeType.Text
			),
		}),
		valueSetter: (params) => {
			const newMedia: AdMedia = typeof params.newValue === 'string' && params.newValue?.length > 0
				? {
					__typename:     'Media',
					mediaUrl:       params.newValue,
					mediaThumbnail: params.newValue,
					mediaType:      'image',
				} : params.newValue;

			if (newMedia === null || doesMediaMatchType(newMedia, params.data.creativeType)) {
				params.data[`creativeContentMediaUrl${index}`] = newMedia;

				if (newMedia === null) {
					params.data[`creativeContentCallToAction${index}`] = undefined;
					params.data[`creativeContentDestinationUrl${index}`] = undefined;
					params.data[`creativeContentDisplayUrl${index}`] = undefined;
					params.data[`creativeContentCaption${index}`] = undefined;
				}

				return true;
			}

			return false;
		},
		...createColumnEditor<AdMedia, AdMedia | undefined | null>(
			{
				cellEditor:   RedditMediaCellEditor,
				columnEditor: RedditMediaColumnEditor,
			},
		),
	},
	{
		columnMode:           ColumnModeTypeEnum.BOTH,
		field:                `creativeContentCaption${index}`,
		headerName:           `${adHeaderNames.creativeContentCaption} ${index + 1}`,
		elasticChild:         'settingsElastic',
		width:                320,
		editable:             params => params.data?.creativeType === CreativeType.Carousel && carouselEditable(params, index),
		conditionallyVisible: (params) => carouselConditionallyVisible(params, index),
		cellRenderer:         DefaultCellRenderer,
		cellRendererParams:   params => ({ disabled: params.data?.creativeType !== CreativeType.Carousel || carouselDisabled(params, index) }),
	},
	{
		columnMode:           ColumnModeTypeEnum.BOTH,
		field:                `creativeContentDestinationUrl${index}`,
		headerName:           `${adHeaderNames.creativeContentDestinationUrl} ${index + 1}`,
		elasticChild:         'settingsElastic',
		width:                400,
		conditionallyVisible: (params) => carouselConditionallyVisible(params, index),
		editable:             params => carouselEditable(params, index) // TODO: add exception for lead generation objective when it is created
			&& params?.data?.parent?.parent?.campaignObjective !== CampaignObjective.AppInstalls,
		cellRenderer:       CreativeContentDestinationUrlCellRenderer,
		cellRendererParams: params => ({
			disabled: carouselDisabled(params, index)
				|| params?.data?.parent?.parent?.campaignObjective === CampaignObjective.AppInstalls,
		}),
	},
	{
		columnMode:           ColumnModeTypeEnum.BOTH,
		field:                `creativeContentDisplayUrl${index}`,
		headerName:           `${adHeaderNames.creativeContentDisplayUrl} ${index + 1}`,
		elasticChild:         'settingsElastic',
		width:                400,
		conditionallyVisible: (params) => carouselConditionallyVisible(params, index),
		editable:             params => carouselEditable(params, index), // TODO: add exception for lead generation objective when it is created
		cellRenderer:         DefaultCellRenderer,
		cellRendererParams:   params => ({ disabled: carouselDisabled(params, index) }),
	},
	{
		columnMode:           ColumnModeTypeEnum.BOTH,
		field:                `creativeContentCallToAction${index}`,
		headerName:           `${adHeaderNames.creativeContentCallToAction} ${index + 1}`,
		elasticChild:         'settingsElastic',
		width:                143,
		conditionallyVisible: (params) => carouselConditionallyVisible(params, index),
		editable:             params => carouselEditable(params, index), // TODO: add exception for lead generation objective when it is created
		cellRenderer:         SelectRenderer,
		cellRendererParams:   params => ({
			disabled: carouselDisabled(params, index),
			options:  getOptionsFromEnum(CreativeContentCallToAction),
		}),
		cellEditor:            SelectCellEditor,
		suppressKeyboardEvent: params => params.editing,
		cellEditorParams:      {
			options: getOptionsFromEnum(CreativeContentCallToAction),
		},
	},
];

const AD_COLUMN_DEFINITIONS: SuperGridColDef[] = [
	{
		columnMode:   ColumnModeTypeEnum.REGULAR,
		field:        'id',
		elasticChild: 'settingsElastic',
		cellRenderer: DefaultCellRenderer,
		hide:         true,
	},
	{
		columnMode:         ColumnModeTypeEnum.BOTH,
		field:              'creativeType',
		colId:              'creativeType',
		headerName:         adHeaderNames.creativeType,
		elasticChild:       'settingsElastic',
		width:              192,
		sortable:           false,
		editable:           params => editableOnThisLevel(params) && params?.data?.isInCreation,
		cellRenderer:       SelectRenderer,
		cellRendererParams: params => ({
			options:  getCreativeTypeByObjective(params.data?.parent?.parent?.campaignObjective),
			disabled: params?.data?.isCreate,
		}),
		cellEditor:       SelectCellEditor,
		cellEditorParams: (params) => ({
			options: getCreativeTypeByObjective(params.data?.parent?.parent?.campaignObjective),
		}),
		suppressKeyboardEvent: params => params.editing,
		valueSetter:           params => {
			params.data.creativeType = params.newValue;

			if (params.newValue !== params.oldValue) {
				[...Array(6).keys()].forEach((index) => {
					params.data[`creativeContentMediaUrl${index}`] = null;
				});

				params.data.creativeText = null;
			}

			return true;
		},
	},
	{
		columnMode:   ColumnModeTypeEnum.BOTH,
		field:        'creativeHeadline',
		colId:        'creativeHeadline',
		headerName:   adHeaderNames.creativeHeadline,
		elasticChild: 'settingsElastic',
		width:        400,
		editable:     params => editableOnThisLevel(params) && creativeFieldEditable(params),
		cellRenderer: DefaultCellRenderer,
	},
	{
		columnMode:   ColumnModeTypeEnum.BOTH,
		field:        'creativeText',
		colId:        'creativeText',
		headerName:   adHeaderNames.creativeText,
		elasticChild: 'settingsElastic',
		width:        400,
		editable:     params => editableOnThisLevel(params)
			&& params?.data?.creativeType === CreativeType.Text
			&& creativeFieldEditable(params),
		cellRenderer:       DefaultCellRenderer,
		cellRendererParams: params => ({
			disabled: params?.data?.isCreate
			|| isFunction(params?.colDef?.editable)
				? !(params?.colDef?.editable as EditableCallback)?.(params as EditableCallbackParams)
				: !params.colDef?.editable
			,
		}),
	},
	{
		columnMode:   ColumnModeTypeEnum.BOTH,
		field:        'profile',
		colId:        'profile',
		elasticChild: 'settingsElastic',
		headerName:   adHeaderNames.profileId,
		width:        300,
		editable:     params => editableOnThisLevel(params) && creativeFieldEditable(params),
		cellRenderer: SelectRenderer,
		cellEditor:   SelectCellEditor,
		valueSetter:  (params) => {
			const newProfile = params?.data?.parent?.parent?.parent?.adAccount?.profiles?.find(
				profile => profile.value === params.newValue,
			);

			if (!newProfile) {
				return false;
			}

			params.data.profile = newProfile;

			return true;
		},
		cellEditorParams: (params) => ({
			options: params?.data?.parent?.parent?.parent?.adAccount?.profiles?.map(profile => ({
				value: profile.profileId,
				label: profile.profileName,
			})) ?? [],
		}),
		cellRendererParams: (params) => ({
			options: params?.data?.parent?.parent?.parent?.adAccount?.profiles?.map(profile => ({
				value: profile.profileId,
				label: profile.profileName,
			})) ?? [],
		}),
	},
	...(flatten(new Array(6).fill(null).map((_, index) => carouselTemplate(index)))),
	{
		columnMode:         ColumnModeTypeEnum.BOTH,
		field:              'creativeThumbnailUrl',
		headerName:         adHeaderNames.creativeThumbnailUrl,
		elasticChild:       'settingsElastic',
		width:              353,
		sortable:           false,
		editable:           params => editableOnThisLevel(params) && CreativeType.Text !== params?.data?.creativeType && creativeFieldEditable(params),
		cellRenderer:       RedditThumbnailCellRenderer,
		cellRendererParams: params => ({ disabled: CreativeType.Text === params?.data?.creativeType || (!creativeFieldEditable(params) && !params.value) }),
		valueSetter:        (params) => {
			const newMedia: AdMedia = typeof params.newValue === 'string' && params.newValue?.length > 0
				? {
					__typename:     'Media',
					mediaUrl:       params.newValue,
					mediaThumbnail: params.newValue,
					mediaType:      'image',
				} : params.newValue;

			if (newMedia === null || doesMediaMatchType(newMedia, CreativeType.Image)) {
				params.data.creativeThumbnailUrl = newMedia?.mediaThumbnail;

				return true;
			}

			return false;
		},
		...createColumnEditor<AdMedia, AdMedia | undefined | null>(
			{
				cellEditor:       RedditMediaCellEditor,
				columnEditor:     RedditMediaColumnEditor,
				cellEditorParams: {
					forceType: CreativeType.Image,
				},
			},
		),
	},
	{
		columnMode:         ColumnModeTypeEnum.BOTH,
		field:              'creativeAllowComments',
		headerName:         adHeaderNames.creativeAllowComments,
		elasticChild:       'settingsElastic',
		width:              353,
		editable:           (params) => editableOnThisLevel(params),
		cellRenderer:       WiseBlueCheckboxCellRenderer,
		cellRendererParams: (params) => (
			{ checkboxDisabled: !(editableOnThisLevel(params)) }
		),
	},
	{
		columnMode:            ColumnModeTypeEnum.BOTH,
		field:                 'adClickEventTrackers',
		colId:                 'adClickEventTrackers',
		headerName:            adHeaderNames.adClickEventTrackers,
		minWidth:              270,
		elasticChild:          'settingsElastic',
		width:                 400,
		sortable:              false,
		editable:              params => editableOnThisLevel(params),
		suppressKeyboardEvent: params => params.editing,
		...createColumnEditor<EventTrackerInput[], EventTrackerInput>(
			{
				cellEditor:       URLsCellEditor,
				cellEditorParams: {
					eventTrackerType: EventTrackerType.Click,
				},
				columnEditor: URLsColumnEditor,
			},
		),
		cellRenderer:       UrlsCellRenderer,
		cellRendererParams: (params) => ({
			eventTrackerType: EventTrackerType.Click,
			showEmpty:        params.data?.isCreate || params.data?.level !== 'ad',
		}),
		valueSetter: (params) => {
			if (typeof params.newValue === 'string' && params.newValue?.length > 0) {
				params.data.adClickEventTrackers = [{
					url:  params.newValue,
					type: EventTrackerType.Click,
				}];

				return true;
			}

			if (isArray(params.newValue) && params.newValue.length) {
				params.data.adClickEventTrackers = params.newValue?.map(tracker => ({
					...tracker,
					type: EventTrackerType.Click,
				}));

				return true;
			}

			params.data.adClickEventTrackers = null;

			return true;
		},
		addEmptyCellsPlaceholder: (params: IUrlsCellRendererParams) => params.showEmpty,
	},
	{
		columnMode:            ColumnModeTypeEnum.BOTH,
		field:                 'adViewEventTrackers',
		headerName:            adHeaderNames.adViewEventTrackers,
		elasticChild:          'settingsElastic',
		width:                 400,
		sortable:              false,
		editable:              params => editableOnThisLevel(params),
		suppressKeyboardEvent: params => params.editing,
		...createColumnEditor<EventTrackerInput[], EventTrackerInput>(
			{
				cellEditor:       URLsCellEditor,
				cellEditorParams: {
					eventTrackerType: EventTrackerType.View,
				},
				columnEditor: URLsColumnEditor,
			},
		),
		cellRenderer:       UrlsCellRenderer,
		cellRendererParams: (params) => ({
			eventTrackerType: EventTrackerType.View,
			showEmpty:        params.data?.isCreate || params.data?.level !== 'ad',
		}),
		valueSetter: (params) => {
			if (typeof params.newValue === 'string') {
				if (params.newValue?.length > 0) {
					params.data.adViewEventTrackers = [{
						url:  params.newValue,
						type: EventTrackerType.View,
					}];
				}
			} else {
				params.data.adViewEventTrackers = params.newValue?.map(tracker => ({
					...tracker,
					type: EventTrackerType.View,
				})) || null;
			}

			return true;
		},
		addEmptyCellsPlaceholder: (params: IUrlsCellRendererParams) => params.showEmpty,
	},
];

export default AD_COLUMN_DEFINITIONS;
